const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getDateKey = (year, monthIndex, day) =>
  year * 10000 + (monthIndex + 1) * 100 + day;

const getUtcTodayKey = () => {
  const now = new Date();
  return getDateKey(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
};

const getWeekdayIndex = (year, monthIndex, day) => {
  const monthOffsets = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
  let adjustedYear = year;
  const month = monthIndex + 1;
  if (month < 3) adjustedYear -= 1;
  return (
    (adjustedYear +
      Math.floor(adjustedYear / 4) -
      Math.floor(adjustedYear / 100) +
      Math.floor(adjustedYear / 400) +
      monthOffsets[month - 1] +
      day) %
    7
  );
};

const normalizeDateOnlyCalendarDay = (dateParts, hasExplicitTimeLabel) => {
  if (!dateParts || hasExplicitTimeLabel || !dateParts.hasTime)
    return dateParts;

  const { hour = 0, minute = 0, second = 0 } = dateParts;
  const hasNonMidnightTime = hour !== 0 || minute !== 0 || second !== 0;
  if (!hasNonMidnightTime) return dateParts;

  // Sitecore date-only fields can be serialized as UTC with an authoring timezone offset.
  // For positive offsets, midnight local may appear as late previous UTC day (e.g. 23:00Z).
  // Shift to next calendar day for late-day UTC times to preserve authored date.
  if (hour >= 12) {
    const shifted = new Date(
      Date.UTC(dateParts.year, dateParts.monthIndex, dateParts.day) +
        24 * 60 * 60 * 1000
    );
    return {
      ...dateParts,
      year: shifted.getUTCFullYear(),
      monthIndex: shifted.getUTCMonth(),
      day: shifted.getUTCDate(),
    };
  }

  return dateParts;
};

const parseDateParts = value => {
  if (!value) return null;

  const input = String(value).trim();
  let match = input.match(/^(\d{4})(\d{2})(\d{2})T\d{2}\d{2}\d{2}Z$/);
  if (match) {
    const year = parseInt(match[1], 10);
    const monthIndex = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const hour = parseInt(input.slice(9, 11), 10);
    const minute = parseInt(input.slice(11, 13), 10);
    const second = parseInt(input.slice(13, 15), 10);
    if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return null;
    return { year, monthIndex, day, hour, minute, second, hasTime: true };
  }

  match = input.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?Z)?$/
  );
  if (match) {
    const year = parseInt(match[1], 10);
    const monthIndex = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const hasTime = Boolean(match[4]);
    const hour = hasTime ? parseInt(match[4], 10) : 0;
    const minute = hasTime ? parseInt(match[5], 10) : 0;
    const second = hasTime && match[6] ? parseInt(match[6], 10) : 0;
    if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return null;
    return { year, monthIndex, day, hour, minute, second, hasTime };
  }

  const parsed = new Date(input);
  if (Number.isNaN(parsed.getTime())) return null;

  return {
    year: parsed.getUTCFullYear(),
    monthIndex: parsed.getUTCMonth(),
    day: parsed.getUTCDate(),
    hour: parsed.getUTCHours(),
    minute: parsed.getUTCMinutes(),
    second: parsed.getUTCSeconds(),
    hasTime: true,
  };
};

export const buildWebinarMetaLabel = item => {
  const parsedDateParts = parseDateParts(item?.Date);
  const dateParts = normalizeDateOnlyCalendarDay(
    parsedDateParts,
    Boolean(item?.Time)
  );
  if (!dateParts) return null;

  const { day, monthIndex, year } = dateParts;
  const month = MONTHS[monthIndex];
  const isUpcoming = getDateKey(year, monthIndex, day) >= getUtcTodayKey();

  if (isUpcoming) {
    const weekday = WEEKDAYS[getWeekdayIndex(year, monthIndex, day)];
    const datePart = `${String(day).padStart(2, '0')} ${month} ${year}`;
    let meta = `${weekday}, ${datePart}`;
    if (item?.Time) meta += ` ${item.Time}`;
    if (item?.Duration) meta += ` (${item.Duration})`;
    return { variant: 'upcoming', meta };
  }

  const monthYear = `${month} ${year}`;
  let meta = monthYear;
  if (item?.Duration) meta += ` (${item.Duration})`;

  return { variant: 'ondemand', meta };
};
